<template>
  <div class="modal-card">
    <section class="modal-card-body p-lg">
      <Loader v-if="loading" :fullPage="false" />
      <div v-else class="container">
        <div class="columns is-multiline">
          <div class="column is-full">
            <span class="subtitle is-3">{{ title }}</span>
          </div>
          <div class="column is-full">
            <b-field label="Источник данных" label-position="on-border">
              <b-select
                placeholder="Выберите источник данных"
                v-model="selectedSerie"
                :loading="loading"
                :disabled="isEditState"
                expanded
              >
                <option v-for="serie in series" :value="serie.id" :key="serie.id">
                  {{ serie.name }}
                </option>
              </b-select>
            </b-field>
          </div>

          <div class="column is-full">
            <b-field
              label="Имя переменной"
              label-position="on-border"
              :message="validate.message"
              :type="validate.type"
            >
              <b-input placeholder="Название переменной" v-model="name" expanded> </b-input>
            </b-field>
          </div>

          <div class="column is-full">
            <b-field>
              <b-radio v-model="declarationType" native-value="setted">
                Задать значение на уровне шаблона
              </b-radio>
            </b-field>

            <b-field>
              <b-radio v-model="declarationType" native-value="unsetted">
                Запрашивать значение при генерации документа
              </b-radio>
            </b-field>

            <b-field v-if="inheritSeries.length >= 1">
              <b-radio v-model="declarationType" native-value="inherited">
                Является зависимой от ранее объявленной переменной
              </b-radio>
            </b-field>
          </div>

          <div class="column">
            <div class="columns">
              <div class="column is-narrow" v-if="levels.length > 1">
                <b-field label-position="on-border">
                  <template #label
                    >Ур..
                    <b-tooltip label="Уровень значения">
                      <b-icon icon="information-outline" size="is-small"></b-icon>
                    </b-tooltip>
                  </template>
                  <b-select v-model="selectedLevel" :disabled="declarationType === 'inherited'">
                    <option v-for="level in levels" :key="level">
                      {{ level }}
                    </option>
                  </b-select>
                </b-field>
              </div>

              <div class="column" v-if="declarationType === 'unsetted'">
                <b-field label-position="on-border">
                  <template #label
                    >Временное значение
                    <b-tooltip
                      label="Будет использовано при генерации графиков и таблиц в режиме предпросмотра"
                      multilined
                    >
                      <b-icon icon="information-outline" size="is-small"></b-icon>
                    </b-tooltip>
                  </template>
                  <b-select placeholder="Значение не задано" v-model="selectedId" :disabled="!selectedSerie" expanded>
                    <option v-for="value in seriesFilteredByLevel" :value="value.id" :key="value.id">
                      {{ value.name }}
                    </option>
                  </b-select>
                </b-field>
              </div>

              <div class="column" v-if="declarationType === 'setted'">
                <b-field label="Значение" label-position="on-border">
                  <b-select placeholder="Значение не задано" v-model="selectedId" :disabled="!selectedSerie" expanded>
                    <option v-for="value in seriesFilteredByLevel" :value="value.id" :key="value.id">
                      {{ value.name }}
                    </option>
                  </b-select>
                </b-field>
              </div>

              <div class="column" v-if="declarationType === 'inherited'">
                <b-field label="Доступные переменные" label-position="on-border">
                  <b-select placeholder="Переменная не задана" v-model="parentId" :disabled="!selectedSerie" expanded>
                    <option v-for="value in inheritSeries" :value="value.internalId" :key="value.internalId">
                      {{ value.name }}
                    </option>
                  </b-select>
                </b-field>
              </div>
            </div>
          </div>

          <div class="column is-full">
            <b-field>
              <b-switch v-model="isBatchVariable" :disabled="isBatchVariableDisabled">
                Может принимать множественные значения
                <b-tooltip label="Шаблон ограничен одной переменной, принимающей множественные значения" multilined>
                  <b-icon icon="information-outline" size="is-small"></b-icon>
                </b-tooltip>
                <b-tooltip label="В разработке">
                  <b-icon icon="information-outline" size="is-small"></b-icon>
                </b-tooltip>
                <div class="is-size-7">
                  Будет использована для пакетной генерации документов
                </div>
              </b-switch>
            </b-field>
          </div>

          <div class="column is-full m-t-md">
            <div class="buttons">
              <b-button @click="$parent.close()">Отмена</b-button>
              <b-button class="button is-primary" :disabled="!selectedSerie" @click="addVariable">Сохранить</b-button>
            </div>
          </div>
        </div>
      </div>
    </section>
  </div>
</template>

<script>
import { mapState, mapActions } from 'vuex';
import { randomId } from '@/plugins/helpers';
import api from '@/api/api';

export default {
  props: {
    codes: {
      type: Array,
      required: true
    },
    codeHandle: {
      type: Object,
    },
    variables: {
      type: Array,
      required: true
    }
  },
  data() {
    return {
      activeTab: 0,
      loading: true,
      title: 'Объявление переменной',
      series: [],
      values: [],
      declarationType: 'setted',
      selectedLevel: 1,
      selectedInherit: null,
      selectedSerie: null,
      selectedId: null,
      parentId: null,
      isBatchVariable: false,
      name: '',
      validate: {
        type: '',
        message: ''
      }
    };
  },
  mounted() {
    this.getData()
  },
  computed: {
    levels() {
      const levels = this.values.map(el => el.level)
      return [...new Set(levels)]
    },

    seriesFilteredByLevel() {
      return this.values.filter(value => value.level == this.selectedLevel);
    },

    inheritSeries() {
      const seriesWithInherits = this.variables
        .filter(variable => variable.data.indicatorId === this.selectedSerie)
        .filter(variable => variable.internalId !== this.codeHandle.internalId);

      const seriesWithParents = seriesWithInherits.filter(serie => {
        const childValue = this.values.find(value => value.id === serie.selectedId);
        const parentValue = this.values.find(value => value.id === childValue.parent_item);
        return parentValue;
      });

      return seriesWithParents || null;
    },

    isBatchVariableDisabled() {
      return true;
    },

    isEditState() {
      return !this.checkObjectForEmpty(this.codeHandle)
    },
  },
  methods: {
    async getData() {
      this.loading = true;

      await api
        .getSeries()
        .then(res => {
          this.setInitialValues(res)
          this.loading = false
        })
        .catch(err => console.log(err))
    },

    setInitialValues(res) {
      this.series = res.data

      if (!this.isEditState) {
        return
      }
      this.selectedSerie = this.codeHandle.data.indicatorId;

      this.name = this.codeHandle.id.substr(1);
      this.selectedLevel = this.codeHandle.selectedLevel || 1;
      this.declarationType = this.codeHandle.type.declarationType || 'setted';
      this.isBatchVariable = this.codeHandle.type.batchVariable || false;
      this.selectedId = this.codeHandle.selectedId || null;
      this.parentId = this.codeHandle.parentId || null;
    },

    validateName() {
      let names = [...this.codes.map(code => code.name), ...this.variables.map(variable => variable.name)];

      if (this.isEditState) {
        names = names.filter(name => name !== this.codeHandle.id.substr(1))
      }

      if (names.some(el => el === this.name)) {
        return false
      }

      this.validate.type = '';
      this.validate.message = '';
      return true;
    },

    validateData() {
      if (this.name === '' || !this.validateName()) {
        this.validate.type = 'is-danger';
        this.validate.message = 'Данное имя недопустимо или уже используется';
        this.toast({
          message: this.validate.message,
          type: this.validate.type,
        })
        return false
      }

      if (this.selectedSerie == null) {
        this.toast({
          message: 'Ошибка, требуется корректно заполнить все поля',
          type: 'is-danger'
        });
        return false;
      }

      if (this.selectedId == null && this.declarationType !== 'inherited') {
        this.toast({
          message: 'Ошибка, требуется заполнить значение',
          type: 'is-danger'
        });
        return false;
      }

      if (this.parentId == null && this.declarationType === 'inherited') {
        this.toast({
          message: 'Ошибка, требуется заполнить значение',
          type: 'is-danger'
        });
        return false;
      }

      return true
    },

    setName(series) {
      if (!this.isEditState) {
        let counter = 1
        this.name = `${series.name} [${counter}]`

        while (!this.validateName()) {
          counter++
          this.name = `${series.name} [${counter}]`
        }
      }
    },

    addVariable() {
      if (!this.validateData()) {
        return
      }

      const checkInternalId = () => {
        return !this.isEditState ? randomId() : this.codeHandle.internalId;
      };

      const checkParentId = () => {
        if (this.declarationType === 'inherited') {
          return this.parentId;
        } else {
          return '';
        }
      };

      const checkBatchStatus = () => {
        if (this.isBatchVariable === true && this.declarationType === 'unsetted') {
          return true;
        } else return false;
      };

      const dataObject = {
        name: this.name,
        internalId: checkInternalId(),
        selectedId: this.selectedId,
        selectedLevel: this.selectedLevel,
        parentId: checkParentId(),
        type: {
          typeName: 'variable',
          declarationType: this.declarationType,
          batchVariable: checkBatchStatus()
        },
        data: {
          indicatorId: this.selectedSerie,
        },
      }

      if (this.isEditState) {
        this.$emit('editShortCode', dataObject);
      } else {
        this.$emit('addShortCode', dataObject);
      }

      this.$parent.close()
    },
  },
  watch: {
    selectedSerie(newValue) {
      const series = this.series.find(el => el.id === newValue)

      this.setName(series)

      if (!this.isEditState) {
        this.selectedId = null;
        this.parentId = null;
      }

      this.values = series.data
    },

    declarationType() {
      if (!this.seriesFilteredByLevel.some(value => value.id === this.selectedId)) {
        this.selectedId = null;
        this.parentId = null;
      }
    },

    parentId(newValue) {
      const childVariable = this.variables.find(value => value.internalId === newValue);
      const childValue = this.values.find(value => value.id === childVariable.selectedId);
      const parentValue = this.values.find(value => value.id === childValue.parent_item);

      this.selectedId = parentValue.id;
      this.selectedLevel = parentValue.level;
    },

    selectedLevel() {
      if (!this.seriesFilteredByLevel.some(value => value.id === this.selectedId)) {
        this.selectedId = null;
        this.parentId = null;
      }
    },

    name() {
      this.validateName()
    },
  },
}
</script>
